
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MapPinCheck } from 'lucide-react';
import { useCurrentRentalPoint, type RentalPoint } from '@/stores/useCurrentRentalPoint';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { dbRentalPointToRentalPoint } from '@/types/entities/rental-point';
import type { DbRentalPoint } from '@/types/entities/rental-point';

export const RentalPointSwitcher: React.FC = () => {
  const { currentRentalPoint, setCurrentRentalPoint, clearCurrentRentalPoint } = useCurrentRentalPoint();
  const { user } = useAuth();
  const [rentalPoints, setRentalPoints] = useState<RentalPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Ждём готовности auth прежде чем грузить пункты
    if (user) {
      fetchRentalPoints(user.id);
      // Интервал удалён - загружаем только при монтировании компонента
      // Это предотвращает спам тост-уведомлений при ошибках
    } else {
      setRentalPoints([]);
      setLoading(false);
    }
  }, [user]);

  const fetchRentalPoints = async (userId: string) => {
    try {
      const { data, error } = await supabaseWorker
        .from('rental_points')
        .select('id, user_id, created_at, updated_at, name, address, phone, email, is_active')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      const rows = (data ?? []) as unknown as DbRentalPoint[];
      const points = rows.map(dbRentalPointToRentalPoint) as RentalPoint[];
      setRentalPoints(points);

      // Если нет доступных пунктов, очищаем текущий пункт
      if (points.length === 0) {
        clearCurrentRentalPoint();
        return;
      }

      // Проверяем, что текущий пункт все еще существует и доступен
      const currentPointExists = currentRentalPoint &&
        points.some((p) => p.id === currentRentalPoint.id);
      
      if (!currentPointExists) {
        // Если текущий пункт не существует или недоступен, выбираем первый
        setCurrentRentalPoint(points[0]);
      }
    } catch (error: unknown) {
      console.error('Error fetching rental points:', error);
      toast({
        title: "Ошибка загрузки",
        description: "Не удалось загрузить пункты проката",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePointSelect = (point: RentalPoint) => {
    setCurrentRentalPoint(point);
    toast({
      title: "Пункт проката изменен",
      description: `Выбран: ${point.name || point.address}`,
    });
  };

  if (loading) {
    return (
      <Button variant="ghost" className="flex items-center gap-2 px-2 h-8 text-sm" disabled>
        <MapPinCheck className="w-4 h-4" />
        <span className="truncate">Загрузка...</span>
      </Button>
    );
  }

  // Если нет доступных пунктов проката
  if (rentalPoints.length === 0) {
    return (
      <Button variant="ghost" className="flex items-center gap-2 px-2 h-8 text-sm cursor-default opacity-60">
        <MapPinCheck className="w-4 h-4" />
        <span className="truncate">Нет пунктов</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-2 h-8 text-sm">
          <MapPinCheck className="w-4 h-4" />
          <span className="truncate">
            {currentRentalPoint?.name || currentRentalPoint?.address || 'Выберите пункт'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {rentalPoints.map((point) => (
          <DropdownMenuItem
            key={point.id}
            onClick={() => handlePointSelect(point)}
            className={currentRentalPoint && point.id === currentRentalPoint.id ? 'bg-accent' : ''}
          >
            <div className="flex flex-col">
              <span className="font-medium">{point.name || 'Без названия'}</span>
              {point.address && (
                <span className="text-xs text-muted-foreground truncate">
                  {point.address}
                </span>
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
